import {
  Info, FileText, TrendingUp, XCircle, AlertCircle, ShoppingCart,
  RefreshCw, CheckCircle2, Users, Receipt, ExternalLink, ListTree,
} from "lucide-react";
import { formatCurrency } from "./bookingConstants";
import s from "./CancellationPricing.module.css";

/* Status text (only) is coloured — everything else stays neutral slate. */
const STATUS_PILL = {
  Cancelled: { cls: s.statusCancelled, label: "Cancelled" },
  Partial: { cls: s.statusPartial, label: "Cancelled (Partial)" },
  Active: { cls: s.statusActive, label: "Active" },
};

export const CancellationStatusPill = ({ status }) => {
  const cfg = STATUS_PILL[status] || STATUS_PILL.Active;
  return (
    <span className={`${s.statusPill} ${cfg.cls}`}>
      <span className={s.dot} />
      {cfg.label}
    </span>
  );
};

const CN_PILL = {
  Issued: s.cnPill,
  Draft: `${s.cnPill} ${s.cnPillDraft}`,
  Pending: `${s.cnPill} ${s.cnPillPending}`,
};

const Tile = ({ label, amount, amountCls, sub, icon, children }) => (
  <div className={s.tile}>
    <div className={s.tileLabel}>{label} <Info /></div>
    {children ? children : <div className={`${s.tileAmount} ${amountCls || s.amtNeutral}`}>{amount}</div>}
    {sub && <div className={s.tileSub}>{sub}</div>}
    <div className={s.tileIcon}>{icon}</div>
  </div>
);

/* ── Top overview block: 8 tiles + (optional) traveller strip + info banner ─ */
export const ProductCancellationOverview = ({ canc }) => {
  if (!canc) return null;
  const hasTravelers = (canc.totalTravelers || 0) > 0;
  const cn = canc.creditNote;

  return (
    <div className={s.cancWrap}>
      <div className={s.tileGrid}>
        <Tile
          label="Original Selling Price"
          amount={formatCurrency(canc.originalSelling)}
          amountCls={s.amtBlue}
          sub="Original booking value"
          icon={<FileText />}
        />
        <Tile
          label="Active Selling Price"
          amount={formatCurrency(canc.activeSelling)}
          amountCls={s.amtActive}
          sub="Remaining active services"
          icon={<TrendingUp />}
        />
        <Tile
          label="Cancelled Value"
          amount={formatCurrency(canc.cancelledValue)}
          amountCls={s.amtCancelled}
          sub="Cancelled services"
          icon={<XCircle />}
        />
        <Tile
          label="Cancellation Fees"
          amount={formatCurrency(canc.totalFees)}
          amountCls={s.amtFees}
          sub="Supplier + agency fees"
          icon={<AlertCircle />}
        />
        <Tile
          label="Active Purchase Price"
          amount={formatCurrency(canc.activePurchase)}
          sub="Paid to suppliers (active only)"
          icon={<ShoppingCart />}
        />
        <Tile
          label="Customer Refund"
          amount={formatCurrency(canc.customerRefund)}
          amountCls={s.amtRefund}
          sub="To be refunded to customer"
          icon={<RefreshCw />}
        />
        <Tile label="Credit Note" sub={cn ? "Credit note issued" : "No credit note yet"} icon={<FileText />}>
          <div className={s.cnValueRow}>
            <span className={s.cnNumberBig}>{cn?.number || "—"}</span>
            {cn?.number && <span className={CN_PILL[cn.status] || s.cnPill}>{cn.status || "Issued"}</span>}
          </div>
        </Tile>
        <Tile
          label="Refund Due"
          amount={formatCurrency(canc.refundDue)}
          amountCls={s.amtRefund}
          sub="Amount to refund to customer"
          icon={<RefreshCw />}
        />
      </div>

      {hasTravelers && (
        <div className={s.statStrip}>
          <div className={s.statItem}>
            <span className={s.statIcon}><Users /></span>
            <span className={s.statText}><span className={s.statLabel}>Total Travelers</span><span className={s.statValue}>{canc.totalTravelers}</span></span>
          </div>
          <div className={s.statItem}>
            <span className={`${s.statIcon} ${s.statIconActive}`}><CheckCircle2 /></span>
            <span className={s.statText}><span className={s.statLabel}>Active Travelers</span><span className={`${s.statValue} ${s.statValueActive}`}>{canc.activeTravelers}</span></span>
          </div>
          <div className={s.statItem}>
            <span className={`${s.statIcon} ${s.statIconCancelled}`}><XCircle /></span>
            <span className={s.statText}><span className={s.statLabel}>Cancelled Travelers</span><span className={`${s.statValue} ${s.statValueCancelled}`}>{canc.cancelledTravelers}</span></span>
          </div>
          <div className={s.statItem}>
            <span className={`${s.statIcon} ${s.statIconActive}`}><CheckCircle2 /></span>
            <span className={s.statText}><span className={s.statLabel}>Active Value</span><span className={`${s.statValue} ${s.statValueActive}`}>{formatCurrency(canc.activeSelling)}</span></span>
          </div>
          <div className={s.statItem}>
            <span className={`${s.statIcon} ${s.statIconCancelled}`}><XCircle /></span>
            <span className={s.statText}><span className={s.statLabel}>Cancelled Value</span><span className={`${s.statValue} ${s.statValueCancelled}`}>{formatCurrency(canc.cancelledValue)}</span></span>
          </div>
          <div className={s.statItem}>
            <span className={`${s.statIcon} ${s.statIconRefund}`}><RefreshCw /></span>
            <span className={s.statText}><span className={s.statLabel}>Refund Due</span><span className={`${s.statValue} ${s.statValueRefund}`}>{formatCurrency(canc.refundDue)}</span></span>
          </div>
        </div>
      )}

      {canc.banner && (
        <div className={s.infoBanner}>
          <Info />
          <span className={s.infoBannerText}>{canc.banner}</span>
        </div>
      )}
    </div>
  );
};

/* ── Read-only block showing the cancelled flight direction's removed rows ─ */
export const CancelledDirectionBlock = ({ dir }) => {
  if (!dir || !dir.rows || dir.rows.length === 0) return null;
  const tot = dir.rows.reduce(
    (a, r) => ({
      net: a.net + (r.net || 0), tax: a.tax + (r.tax || 0), vat: a.vat + (r.vat || 0),
      commission: a.commission + (r.commission || 0), segFee: a.segFee + (r.segFee || 0),
      clientPrice: a.clientPrice + (r.clientPrice || 0),
    }),
    { net: 0, tax: 0, vat: 0, commission: 0, segFee: 0, clientPrice: 0 }
  );
  return (
    <div className={s.cancelledDir}>
      <div className={s.cancelledDirHead}>
        <span className={s.cancelledDirBadge}>{(dir.direction || "").toUpperCase()}</span>
        {dir.route && <span className={s.cancelledDirRoute}>{dir.route}</span>}
        {dir.meta && <span className={s.cancelledDirMeta}>{dir.meta}</span>}
        <span className={s.cancelledDirStatus}><span className={s.dot} />Status: Cancelled</span>
      </div>
      <div className={s.cdTable}>
        <div className={s.cdHeadRow}>
          <span>#</span><span>Traveler</span>
          <span style={{ textAlign: "right" }}>Net Price</span>
          <span style={{ textAlign: "right" }}>Tax</span>
          <span style={{ textAlign: "right" }}>VAT</span>
          <span style={{ textAlign: "right" }}>Commission</span>
          <span style={{ textAlign: "right" }}>Client Price</span>
        </div>
        {dir.rows.map((r, i) => (
          <div key={i} className={s.cdRow}>
            <span className={s.cdNum}>{i + 1}</span>
            <span><span className={s.cdName}>{r.name}</span></span>
            <span className={`${s.cdVal} ${s.cdStrike}`}>{formatCurrency(r.net)}</span>
            <span className={`${s.cdVal} ${s.cdStrike}`}>{formatCurrency(r.tax)}</span>
            <span className={`${s.cdVal} ${s.cdStrike}`}>{formatCurrency(r.vat)}</span>
            <span className={`${s.cdVal} ${s.cdStrike}`}>{formatCurrency(r.commission)}</span>
            <span className={`${s.cdVal} ${s.cdStrike}`}>{formatCurrency(r.clientPrice)}</span>
          </div>
        ))}
        <div className={s.cdTotalRow}>
          <span className={s.cdTotalLabel}>Cancelled Total</span>
          <span className={s.cdTotalVal}>{formatCurrency(tot.net)}</span>
          <span className={s.cdTotalVal}>{formatCurrency(tot.tax)}</span>
          <span className={s.cdTotalVal}>{formatCurrency(tot.vat)}</span>
          <span className={s.cdTotalVal}>{formatCurrency(tot.commission)}</span>
          <span className={s.cdTotalVal}>{formatCurrency(tot.clientPrice)}</span>
        </div>
      </div>
    </div>
  );
};

/* ── Bottom summary card (values / fees / refund / credit note) ─────────── */
export const CancellationSummaryCard = ({ canc, onViewCreditNote }) => {
  if (!canc) return null;
  const cn = canc.creditNote;
  return (
    <div className={s.summaryCard}>
      <div className={s.summaryHead}><Receipt /> Cancellation Summary</div>
      <div className={s.summaryGrid}>
        <div className={s.summaryCol}>
          <div className={s.sumRow}><span className={s.sumLabel}>Original Value</span><span className={s.sumValue}>{formatCurrency(canc.originalSelling)}</span></div>
          <div className={s.sumRow}><span className={s.sumLabel}>Cancelled Value</span><span className={`${s.sumValue} ${s.sumValueCancelled}`}>{formatCurrency(canc.cancelledValue)}</span></div>
          <div className={s.sumRow}><span className={s.sumLabel}>Active Value</span><span className={`${s.sumValue} ${s.sumValueActive}`}>{formatCurrency(canc.activeSelling)}</span></div>
        </div>
        <div className={s.summaryCol}>
          <div className={s.sumRow}><span className={s.sumLabel}>Supplier Cancellation Fee</span><span className={s.sumValue}>{formatCurrency(canc.supplierFee)}</span></div>
          <div className={s.sumRow}><span className={s.sumLabel}>Sunsky Cancellation Fee</span><span className={s.sumValue}>{formatCurrency(canc.agencyFee)}</span></div>
          <div className={s.sumDivider} />
          <div className={s.sumRow}><span className={s.sumLabel}>Total Cancellation Fees</span><span className={`${s.sumValue} ${s.sumValueFees}`}>{formatCurrency(canc.totalFees)}</span></div>
        </div>
        <div className={s.summaryCol}>
          <div className={s.refundLabel}>Refund Due to Customer</div>
          <div className={s.refundValue}>{formatCurrency(canc.refundDue)}</div>
          <div className={s.refundSub}>To be refunded to customer</div>
        </div>
        <div className={s.summaryCol}>
          <div className={s.cnLabel}>Credit Note</div>
          <div className={s.cnRow}>
            <span className={s.cnNumber}>{cn?.number || "—"}</span>
            {cn?.number && <span className={CN_PILL[cn.status] || s.cnPill}>{cn.status || "Issued"}</span>}
          </div>
          {cn?.issuedOn && <div className={s.cnDate}>Issued on: {cn.issuedOn}</div>}
          {cn?.number && (
            <button type="button" className={s.viewCnBtn} onClick={onViewCreditNote}>
              <Receipt /> View Credit Note
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════
   Booking-level Price Breakdown (After Cancellation)
   ════════════════════════════════════════════════════════════════════════ */
const pct = (val, base) => (base > 0 ? `(${((val / base) * 100).toFixed(1).replace(".", ",")}%)` : "(0%)");

export const PriceBreakdownAfterCancellation = ({ breakdown, onViewFull }) => {
  if (!breakdown || !breakdown.rows) return null;
  const { rows, totals, creditNote } = breakdown;
  const cn = creditNote;

  return (
    <div className={s.bdCard}>
      <div className={s.bdHead}>
        <div className={s.bdTitle}><ListTree /> Price Breakdown (After Cancellation)</div>
        {onViewFull && (
          <button type="button" className={s.bdViewBtn} onClick={onViewFull}>
            <ExternalLink /> View Full Pricing Details
          </button>
        )}
      </div>

      <div className={s.bdInfo}>
        <Info />
        This breakdown shows the original booking amounts, the cancelled amounts, and the final active amounts.
      </div>

      <div className={s.bdTable}>
        <div className={s.bdHeadRow}>
          <span>Description</span>
          <span>Original (EUR)</span>
          <span>Cancelled (EUR)</span>
          <span>Cancellation Fees (EUR)</span>
          <span>Active (EUR)</span>
          <span>Refund Due (EUR)</span>
        </div>
        {rows.map((r) => (
          <div key={r.label} className={s.bdRow}>
            <span className={s.bdCellDesc}>{r.label}</span>
            <span className={s.bdCell}><span className={`${s.bdAmt} ${s.colOriginal}`}>{formatCurrency(r.original)}</span><span className={s.bdPct}>(100%)</span></span>
            <span className={s.bdCell}><span className={`${s.bdAmt} ${s.colCancelled}`}>{formatCurrency(r.cancelled)}</span><span className={s.bdPct}>{pct(r.cancelled, r.original)}</span></span>
            <span className={s.bdCell}><span className={`${s.bdAmt} ${s.colFees}`}>{formatCurrency(r.fees)}</span><span className={s.bdPct}>{pct(r.fees, r.original)}</span></span>
            <span className={s.bdCell}><span className={`${s.bdAmt} ${s.colActive}`}>{formatCurrency(r.active)}</span><span className={s.bdPct}>{pct(r.active, r.original)}</span></span>
            <span className={s.bdCell}><span className={`${s.bdAmt} ${s.colRefund}`}>{formatCurrency(r.refundDue)}</span></span>
          </div>
        ))}
        <div className={s.bdTotalRow}>
          <span className={s.bdCellDesc}>TOTAL</span>
          <span className={s.bdCell}><span className={`${s.bdAmt} ${s.colOriginal}`}>{formatCurrency(totals.original)}</span><span className={s.bdPct}>(100%)</span></span>
          <span className={s.bdCell}><span className={`${s.bdAmt} ${s.colCancelled}`}>{formatCurrency(totals.cancelled)}</span><span className={s.bdPct}>{pct(totals.cancelled, totals.original)}</span></span>
          <span className={s.bdCell}><span className={`${s.bdAmt} ${s.colFees}`}>{formatCurrency(totals.fees)}</span><span className={s.bdPct}>{pct(totals.fees, totals.original)}</span></span>
          <span className={s.bdCell}><span className={`${s.bdAmt} ${s.colActive}`}>{formatCurrency(totals.active)}</span><span className={s.bdPct}>{pct(totals.active, totals.original)}</span></span>
          <span className={s.bdCell}><span className={`${s.bdAmt} ${s.colRefund}`}>{formatCurrency(totals.refundDue)}</span></span>
        </div>
      </div>

      <div className={s.bdFooter}>
        <div className={s.bdFootTile}>
          <span className={`${s.bdFootIcon} ${s.bdFootIconActive}`}><CheckCircle2 /></span>
          <span className={s.bdFootText}>
            <span className={s.bdFootLabel}>Active Total</span>
            <span className={`${s.bdFootValue} ${s.colActive}`}>{formatCurrency(totals.active)}</span>
            <span className={s.bdFootSub}>{pct(totals.active, totals.original).replace(/[()]/g, "")} of original</span>
          </span>
        </div>
        <div className={s.bdFootTile}>
          <span className={`${s.bdFootIcon} ${s.bdFootIconCancelled}`}><XCircle /></span>
          <span className={s.bdFootText}>
            <span className={s.bdFootLabel}>Cancelled Total</span>
            <span className={`${s.bdFootValue} ${s.colCancelled}`}>{formatCurrency(totals.cancelled)}</span>
            <span className={s.bdFootSub}>{pct(totals.cancelled, totals.original).replace(/[()]/g, "")} of original</span>
          </span>
        </div>
        <div className={s.bdFootTile}>
          <span className={`${s.bdFootIcon} ${s.bdFootIconFees}`}><AlertCircle /></span>
          <span className={s.bdFootText}>
            <span className={s.bdFootLabel}>Cancellation Fees</span>
            <span className={`${s.bdFootValue} ${s.colFees}`}>{formatCurrency(totals.fees)}</span>
            <span className={s.bdFootSub}>{pct(totals.fees, totals.original).replace(/[()]/g, "")} of original</span>
          </span>
        </div>
        <div className={s.bdFootTile}>
          <span className={`${s.bdFootIcon} ${s.bdFootIconRefund}`}><RefreshCw /></span>
          <span className={s.bdFootText}>
            <span className={s.bdFootLabel}>Refund Due to Customer</span>
            <span className={`${s.bdFootValue} ${s.amtRefund}`}>{formatCurrency(totals.refundDue)}</span>
            <span className={s.bdFootSub}>Amount to be refunded</span>
          </span>
        </div>
      </div>

      {cn?.number && (
        <div className={s.bdCnFooter}>
          <span className={s.bdCnNote}>All cancelled values and fees are included in the credit note. Refund will be processed through the client payment method.</span>
          <div className={s.bdCnMeta}>
            <div className={s.bdCnMetaItem}>
              <span className={s.bdCnMetaLabel}>Credit Note</span>
              <span className={s.bdCnMetaValue}>{cn.number}{cn.status && <span className={CN_PILL[cn.status] || s.cnPill}>{cn.status}</span>}</span>
            </div>
            {cn.issuedOn && (
              <div className={s.bdCnMetaItem}>
                <span className={s.bdCnMetaLabel}>Issued On</span>
                <span className={`${s.bdCnMetaValue} ${s.bdCnMetaValueInk}`}>{cn.issuedOn}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
